import { useState, useEffect, useRef, useLayoutEffect, useCallback } from 'react';

interface NavItem {
  key: string;
  href: string;
  label: string;
}

interface UseNavOverflowResult {
  visibleItems: NavItem[];
  overflowItems: NavItem[];
  measurementComplete: boolean;
}

export const useNavOverflow = (
  items: NavItem[],
  navContainerRef: React.RefObject<HTMLElement>,
  menuLabel: string,
  enabled: boolean = true
): UseNavOverflowResult => {
  const [visibleItems, setVisibleItems] = useState<NavItem[]>(items);
  const [overflowItems, setOverflowItems] = useState<NavItem[]>([]);
  const [measurementComplete, setMeasurementComplete] = useState(false);
  const lastWidthRef = useRef(0);
  const debounceRef = useRef<NodeJS.Timeout>();

  const priorityOrder = ['how_it_works', 'for_couriers', 'support', 'faq', 'about'];

  const sortedItems = [...items].sort((a, b) => {
    const aIdx = priorityOrder.indexOf(a.key);
    const bIdx = priorityOrder.indexOf(b.key);
    return (aIdx === -1 ? 999 : aIdx) - (bIdx === -1 ? 999 : bIdx);
  });

  const measure = useCallback(() => {
    if (!navContainerRef.current || !enabled) return;

    const container = navContainerRef.current;
    const containerWidth = container.clientWidth;
    if (containerWidth === 0) return;

    // Skip if width hasn't changed
    if (Math.abs(lastWidthRef.current - containerWidth) < 4) return;
    lastWidthRef.current = containerWidth;

    // Measure each item width using an offscreen element
    const measureEl = document.createElement('span');
    measureEl.style.cssText = 'position:absolute;visibility:hidden;height:0;overflow:hidden;white-space:nowrap;font-size:14px;font-weight:600;padding:0 12px;';
    document.body.appendChild(measureEl);

    const widths = sortedItems.map(item => {
      measureEl.textContent = item.label;
      return { key: item.key, width: measureEl.getBoundingClientRect().width + 8 }; // +8 gap
    });

    measureEl.textContent = menuLabel + ' ▾';
    const moreWidth = measureEl.getBoundingClientRect().width + 16;
    document.body.removeChild(measureEl);

    const totalWidth = widths.reduce((s, w) => s + w.width, 0);

    if (totalWidth <= containerWidth) {
      setVisibleItems(sortedItems);
      setOverflowItems([]);
    } else {
      const adjustedWidth = containerWidth - moreWidth;
      let used = 0;
      const visible: NavItem[] = [];
      const overflow: NavItem[] = [];

      for (let i = 0; i < sortedItems.length; i++) {
        const w = widths.find(w => w.key === sortedItems[i].key)?.width || 0;
        if (used + w <= adjustedWidth) {
          visible.push(sortedItems[i]);
          used += w;
        } else {
          overflow.push(sortedItems[i]);
        }
      }

      setVisibleItems(visible);
      setOverflowItems(overflow);
    }

    setMeasurementComplete(true);
  }, [items, enabled, menuLabel]);

  const debouncedMeasure = useCallback(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(measure, 80);
  }, [measure]);

  // Initial measurement
  useLayoutEffect(() => {
    if (!enabled) {
      setVisibleItems(items);
      setOverflowItems([]);
      setMeasurementComplete(true);
      return;
    }
    requestAnimationFrame(() => setTimeout(measure, 30));
  }, [items, enabled, measure]);

  // Resize observer
  useEffect(() => {
    if (!enabled || !navContainerRef.current) return;
    const ro = new ResizeObserver(debouncedMeasure);
    ro.observe(navContainerRef.current);
    return () => {
      ro.disconnect();
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [enabled, debouncedMeasure]);

  return { visibleItems, overflowItems, measurementComplete };
};
